import React, { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { ArrowLeft, Save, X } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useFinancasStore } from '@/stores/financas.store'
import { useAuth } from '@/hooks/use-auth-wrapper'

const tiposDespesa = [
  { value: 'energia', label: 'Energia Elétrica' },
  { value: 'agua', label: 'Água e Esgoto' },
  { value: 'telefone', label: 'Telefone' },
  { value: 'internet', label: 'Internet' },
  { value: 'limpeza', label: 'Material de Limpeza' },
  { value: 'seguranca', label: 'Segurança' },
  { value: 'combustivel', label: 'Combustível' },
  { value: 'manutencao', label: 'Manutenção Geral' },
  { value: 'outros', label: 'Outros' }
]

export function CadastrarDespesaOperacionalPage() {
  const navigate = useNavigate()
  const { addDespesaOperacional } = useFinancasStore()
  const { user } = useAuth()
  
  const [formData, setFormData] = useState({
    tipo: '',
    descricao: '',
    valor: '',
    data: new Date().toISOString().split('T')[0],
    dataVencimento: '',
    responsavel: user?.email || '',
    observacoes: ''
  })

  const [isLoading, setIsLoading] = useState(false)

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.tipo || !formData.descricao || !formData.valor || !formData.dataVencimento) {
      alert('Por favor, preencha todos os campos obrigatórios.')
      return
    }

    setIsLoading(true)

    try {
      const novaDespesa = {
        tipo: formData.tipo as any,
        descricao: formData.descricao,
        valor: parseFloat(formData.valor),
        data: formData.data,
        dataVencimento: formData.dataVencimento,
        responsavel: formData.responsavel,
        observacoes: formData.observacoes,
        status: 'pendente' as const
      }

      addDespesaOperacional(novaDespesa)
      
      // Redirecionar para a listagem
      navigate('/financas/administracao/despesas-operacionais')
    } catch (error) {
      console.error('Erro ao cadastrar despesa:', error)
      alert('Erro ao cadastrar despesa. Tente novamente.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCancel = () => {
    navigate('/financas/administracao/despesas-operacionais')
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleCancel}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Voltar
        </Button>
        <div>
          <h1 className="text-2xl font-bold">Cadastrar Despesa Operacional</h1>
          <p className="text-muted-foreground">
            Adicione uma nova despesa operacional da igreja
          </p>
        </div>
      </div>

      {/* Formulário */}
      <Card>
        <CardHeader>
          <CardTitle>Informações da Despesa</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="tipo">Tipo de Despesa *</Label>
                <Select 
                  value={formData.tipo} 
                  onValueChange={(value) => handleInputChange('tipo', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    {tiposDespesa.map(tipo => (
                      <SelectItem key={tipo.value} value={tipo.value}>
                        {tipo.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="valor">Valor *</Label>
                <Input
                  id="valor"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0,00"
                  value={formData.valor}
                  onChange={(e) => handleInputChange('valor', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="data">Data da Despesa *</Label>
                <Input
                  id="data"
                  type="date"
                  value={formData.data}
                  onChange={(e) => handleInputChange('data', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="dataVencimento">Data de Vencimento *</Label>
                <Input
                  id="dataVencimento"
                  type="date"
                  value={formData.dataVencimento}
                  onChange={(e) => handleInputChange('dataVencimento', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="responsavel">Responsável *</Label>
                <Input
                  id="responsavel"
                  placeholder="Nome do responsável"
                  value={formData.responsavel}
                  onChange={(e) => handleInputChange('responsavel', e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="descricao">Descrição *</Label>
              <Input
                id="descricao"
                placeholder="Descrição da despesa"
                value={formData.descricao}
                onChange={(e) => handleInputChange('descricao', e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="observacoes">Observações</Label>
              <Textarea
                id="observacoes"
                placeholder="Observações adicionais (opcional)"
                value={formData.observacoes}
                onChange={(e) => handleInputChange('observacoes', e.target.value)}
                rows={3}
              />
            </div>

            {/* Botões */}
            <div className="flex gap-4 pt-4">
              <Button
                type="submit"
                disabled={isLoading}
                className="flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                {isLoading ? 'Salvando...' : 'Salvar Despesa'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={isLoading}
                className="flex items-center gap-2"
              >
                <X className="w-4 h-4" />
                Cancelar
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}